import type {
  RpcError,
  RpcEvent,
  RpcMessage,
  RpcRequest,
  RpcResponse,
} from '@hibro/shared';

/**
 * Transport-agnostic, point-to-point message link (架构方案 §5.2). Concrete
 * adapters wrap a DOM `MessagePort` (renderer) or an Electron `MessagePortMain`
 * (main / extension host) so the Channel layer stays free of platform deps.
 */
export interface IMessageTransport {
  send(message: RpcMessage): void;
  /** Subscribe to incoming messages; returns a disposer. */
  onMessage(handler: (message: RpcMessage) => void): () => void;
}

export type RequestHandler = (...args: unknown[]) => unknown;

/** Error fields carried explicitly; anything else own + primitive goes to `data`. */
const RPC_ERROR_OWN_FIELDS = new Set(['name', 'message', 'stack']);

/**
 * Collect the error's own primitive-valued properties (a native return code, an
 * HTTP status, a domain error id). Only primitives cross: the caller needs a
 * verdict to branch on, not the object graph the throw site happened to hold —
 * and a `MessagePort` would reject non-clonable values outright.
 */
function toErrorData(err: Error): RpcError['data'] {
  let data: Record<string, string | number | boolean | null> | undefined;
  for (const key of Object.keys(err)) {
    if (RPC_ERROR_OWN_FIELDS.has(key)) {
      continue;
    }
    const value = (err as unknown as Record<string, unknown>)[key];
    if (
      value === null ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      data ??= {};
      data[key] = value;
    }
  }
  return data;
}

/**
 * Flatten a thrown value for the wire. `name` + `data` are what make the error
 * *actionable* on the far side: a subclass's prototype cannot cross a port, so
 * without them every failure arrives as an anonymous `Error` carrying prose, and
 * callers that need to tell "server rejected it" from "network hiccup" have no
 * choice but to parse the message text.
 */
function toRpcError(err: unknown): RpcError {
  if (err instanceof Error) {
    const data = toErrorData(err);
    return {
      name: err.name,
      message: err.message,
      stack: err.stack,
      ...(data ? { data } : {}),
    };
  }
  return { message: String(err) };
}

/** Rebuild a rejection from the wire, re-attaching `name` and the `data` fields. */
function fromRpcError(error: RpcError): Error {
  const err = new Error(error.message);
  if (error.name) {
    err.name = error.name;
  }
  if (error.data) {
    Object.assign(err, error.data);
  }
  return err;
}

/**
 * Serves RPC requests over a transport. Multiple servers can share the same
 * business state by registering handlers that delegate to a shared object.
 */
export class ChannelServer {
  private readonly handlers = new Map<string, RequestHandler>();
  private readonly dispose: () => void;

  constructor(private readonly transport: IMessageTransport) {
    this.dispose = transport.onMessage((message) => {
      void this.onMessage(message);
    });
  }

  handle(method: string, handler: RequestHandler): void {
    this.handlers.set(method, handler);
  }

  /**
   * Push an event to the peer over this connection (架构方案 §5.2 listen /
   * §5.15 EventBus). Fire-and-forget: the client filters by event name locally.
   */
  emit(event: string, data: unknown): void {
    const message: RpcEvent = { kind: 'event', event, data };
    this.transport.send(message);
  }

  close(): void {
    this.dispose();
  }

  private async onMessage(message: RpcMessage): Promise<void> {
    if (message.kind !== 'request') {
      return;
    }

    const request: RpcRequest = message;
    const handler = this.handlers.get(request.method);
    if (!handler) {
      this.respond(request.id, undefined, {
        message: `No handler registered for method: ${request.method}`,
      });
      return;
    }

    try {
      const result = await handler(...request.args);
      this.respond(request.id, result);
    } catch (err) {
      this.respond(request.id, undefined, toRpcError(err));
    }
  }

  private respond(id: number, result?: unknown, error?: RpcError): void {
    const response: RpcResponse = { kind: 'response', id, result, error };
    this.transport.send(response);
  }
}

/**
 * Calls RPC methods over a transport, matching responses to pending requests
 * by an incrementing sequence id.
 */
export type EventHandler<T = unknown> = (data: T) => void;

export class ChannelClient {
  private seq = 0;
  private readonly pending = new Map<
    number,
    { resolve: (value: unknown) => void; reject: (reason: unknown) => void }
  >();
  private readonly listeners = new Map<string, Set<EventHandler>>();
  private readonly dispose: () => void;

  constructor(private readonly transport: IMessageTransport) {
    this.dispose = transport.onMessage((message) => {
      this.onMessage(message);
    });
  }

  call<T = unknown>(method: string, ...args: unknown[]): Promise<T> {
    const id = ++this.seq;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
      });
      this.transport.send({ kind: 'request', id, method, args });
    });
  }

  /**
   * Subscribe to server-pushed events by name (架构方案 §5.2 listen). Returns a
   * disposer; subscription is local (the server broadcasts, the client filters).
   */
  listen<T = unknown>(event: string, handler: EventHandler<T>): () => void {
    let handlers = this.listeners.get(event);
    if (!handlers) {
      handlers = new Set();
      this.listeners.set(event, handlers);
    }
    handlers.add(handler as EventHandler);
    return () => {
      const set = this.listeners.get(event);
      if (!set) {
        return;
      }
      set.delete(handler as EventHandler);
      if (set.size === 0) {
        this.listeners.delete(event);
      }
    };
  }

  close(): void {
    this.dispose();
    for (const { reject } of this.pending.values()) {
      reject(new Error('Channel closed'));
    }
    this.pending.clear();
    this.listeners.clear();
  }

  private onMessage(message: RpcMessage): void {
    if (message.kind === 'event') {
      const handlers = this.listeners.get(message.event);
      if (handlers) {
        for (const handler of handlers) {
          handler(message.data);
        }
      }
      return;
    }

    if (message.kind !== 'response') {
      return;
    }

    const entry = this.pending.get(message.id);
    if (!entry) {
      return;
    }
    this.pending.delete(message.id);

    if (message.error) {
      entry.reject(fromRpcError(message.error));
    } else {
      entry.resolve(message.result);
    }
  }
}
